import {useQueryClient} from '@tanstack/react-query';
import {useSelector} from '@xstate/react';
import {useRouter} from 'next/router';
import {useCallback, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {createActor} from 'xstate';
import {requestContext} from '../../../../common/requestContext';
import {
  COMMAND_KEY,
  runCommand,
} from '../../../../common/shared/hooks/useCommandWithLocalStorage';
import {IS_CLIENT_SIDE} from '../../../../common/shared/utils/constants';
import {isTrue} from '../../../../common/shared/utils/isTrue';
import {useRouteChangeStarted} from '../../../../common/useRouteChangeStarted.hook';
import useTypedSelector from '../../../../common/useTypedSelector';
import {useWrapRef} from '../../../../common/useWrapRef.hook';
import {
  Maybe,
  QueryFromPage,
  TicketInfo,
  TicketType,
} from '../../../../graphql/generated/types';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {useLogin} from '../../../../user/useLogin.hook';
import {useViewerInfoQuery} from '../../../../wrapper/viewerWrapper';
import {setTicketFlowInProgress} from '../../../content/[seriesId]/viewer/[productId]/state/viewerInfoSlice';
import {
  LOCAL_STORAGE_BUY_TICKET_FLOW_DEBUG,
  QUERY_KEY_VIEWER_TICKET_TYPE_TO_USE,
} from '../constant';
import {
  buyTicketFlowMachine,
  getXstateInspector,
} from '../machine/buyTicketMachine';
import {getTicketPopupOn} from '../util/ticketPopupOn';

/**
 * *톡뷰어 외에선 사용하지 말것*
 * talkViewerEventSaga 에서는 hook 을 사용할 수 없기 때문에 땜빵용으로 넣은 변수입니다. 글로벌하게 사용하면 초기화 타이밍 이슈 등으로 문제가 발생할 수 있습니다.
 * 톡뷰어는 이미 서비스 종료되었지만 소장회차에 대해서만 접근할 수 있습니다. 차후 소장품 기간이 종료되어 코드 제거해도 되게 되면 같이 제거합니다.
 *
 */
export let doNotUseThisStartTicketProcess:
  | ((param: StartTicketProcessParam) => void)
  | undefined;

interface StartTicketProcessParam {
  seriesId: number;
  currentProductId?: Maybe<number>;
  targetProductId: number;
  queryFrom: QueryFromPage;
  nonstopWatching?: boolean;
  pickExactly: boolean;
  expiredConfirmed?: Maybe<boolean>;
  storedTicketInfo?: TicketInfo;
}

const actor = createActor(buyTicketFlowMachine, {
  ...(IS_CLIENT_SIDE &&
    isTrue(localStorage?.getItem(LOCAL_STORAGE_BUY_TICKET_FLOW_DEBUG)) && {
      inspect: {
        next: value => {
          switch (value.type) {
            case '@xstate.event': {
              console.log(
                '%c[xstate]%c[event]',
                'color: green;',
                'color: #192;',
                `[${value.event.type}]`,
                value
              );
              break;
            }
            case '@xstate.actor': {
              console.log(
                '%c[xstate]%c[actor]',
                'color: green;',
                'color: orange;',
                `[src=${value.actorRef.sessionId}]`,
                value
              );
              break;
            }
            case '@xstate.snapshot': {
              console.log(
                '%c[xstate]%c[snapshot]',
                'color: green;',
                'color: cyan;',
                `[cause=${value.event.type}]`,
                value
              );
              break;
            }
            case '@xstate.microstep': {
              // skip
              break;
            }
            default: {
              console.log(
                '%c[xstate]%c[unknown]',
                'color: green;',
                'color: #478;',
                value
              );
              break;
            }
          }
          getXstateInspector()?.inspect?.next?.(value);
        },
        error: error => {
          console.log(
            '%c[xstate]%c[error]',
            'color: green;',
            'color: red;',
            error
          );
          getXstateInspector()?.inspect?.error?.(error);
        },
        complete: () => {
          console.log('%c[xstate][complete]', 'color: green;');
          getXstateInspector()?.inspect?.complete?.();
        },
      },
    }),
}).start();

export function useBuyTicketFlow(param?: {
  onInit?: Function;
  onSuccess?: Function;
  onError?: Function;
  disableCommand?: boolean;
}) {
  const send = actor.send;
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const login = useLogin();
  const router = useRouter();
  const {nonstopWatchingInSeries, ticketFlowInProgress, seriesId, productId} =
    useTypedSelector(state => ({
      nonstopWatchingInSeries: state.viewerInfo.nonstopWatchingInSeries,
      ticketFlowInProgress: state.viewerInfo.ticketFlowInProgress,
      seriesId: state.viewerInfo.currentSeriesId,
      productId: state.viewerInfo.currentProductId,
    }));
  const viewerInfo = useViewerInfoQuery(
    // @ts-ignore
    {seriesId, productId},
    ONLY_DATA_CONFIG
  ).data;

  const isShortsDrama = !!productId && !!viewerInfo?.item?.isShortsDrama;
  useRouteChangeStarted({
    callback: () => {
      send({type: '취소'});
    },
  });

  const onInitRef = useWrapRef(param?.onInit);
  const onSuccessRef = useWrapRef(param?.onSuccess);
  const onErrorRef = useWrapRef(param?.onError);

  const startSetBuyTicketProcess = useCallback(
    ({
      seriesId,
      productId,
      ticketType,
      setId,
      setTitle,
      seriesTitle,
      queryFrom,
      storedTicketInfo,
    }: {
      seriesId: number;
      productId?: number;
      ticketType?: Maybe<TicketType>;
      setId: number;
      setTitle: string;
      seriesTitle: string;
      queryFrom: QueryFromPage;
      storedTicketInfo?: TicketInfo;
    }) => {
      if (ticketFlowInProgress) {
        return;
      }

      dispatch(setTicketFlowInProgress(true));

      send({
        type: '세트구매요청됨',
        isMobile: requestContext.requestInfo.display.isMobile,
        user: login.user,
        buySetProcessParam: {
          seriesId,
          ticketType,
          productId,
          setId,
          setTitle,
          seriesTitle,
          queryFrom,
        },
        queryClient,
        dispatch,
        storedTicketInfo,
        isShortsDrama,
        onInit: onInitRef.current,
        onSuccess: onSuccessRef.current,
        onError: onErrorRef.current,
      });
    },
    [
      dispatch,
      login.user,
      queryClient,
      send,
      onInitRef,
      onSuccessRef,
      onErrorRef,
      ticketFlowInProgress,
      isShortsDrama,
    ]
  );

  const startTicketProcess = useCallback(
    ({
      seriesId,
      currentProductId,
      targetProductId,
      queryFrom,
      nonstopWatching,
      pickExactly,
      expiredConfirmed,
      storedTicketInfo,
    }: StartTicketProcessParam) => {
      if (ticketFlowInProgress) {
        return;
      }

      const popupOn = !!getTicketPopupOn();
      const ticketTypeToUseQuery = router.query[
        QUERY_KEY_VIEWER_TICKET_TYPE_TO_USE
      ] as TicketType | undefined;

      dispatch(setTicketFlowInProgress(true));

      send({
        type: '이용권플로우요청됨',
        isMobile: requestContext.requestInfo.display.isMobile,
        user: login.user,
        ticketProcessParam: {
          seriesId,
          productId: targetProductId,
          queryFrom,
          nonstopWatching,
          pickExactly,
          popupOn,
          ticketTypeToUseQuery,
        },
        queryClient,
        dispatch,
        currentProductId,
        selectedNonstopWatchingBuyTicketInfo:
          nonstopWatchingInSeries[seriesId]?.selectedBuyTicketInfo,
        expiredConfirmed,
        storedTicketInfo,
        isShortsDrama,
        onInit: onInitRef.current,
        onSuccess: onSuccessRef.current,
        onError: onErrorRef.current,
      });
    },
    [
      dispatch,
      router.query,
      login.user,
      nonstopWatchingInSeries,
      queryClient,
      send,
      onInitRef,
      onSuccessRef,
      onErrorRef,
      ticketFlowInProgress,
      isShortsDrama,
    ]
  );
  doNotUseThisStartTicketProcess = startTicketProcess;

  useEffect(() => {
    if (param?.disableCommand) {
      return;
    }

    runCommand({
      commandKey: COMMAND_KEY.CLICK_BUY_TICKETS,
      action: async commandData => {
        startTicketProcess({
          ...commandData,
          currentProductId: commandData.currentProductId,
          targetProductId: commandData.productId,
          ...(onInitRef.current && {onInit: onInitRef.current}),
          ...(onSuccessRef.current && {onSuccess: onSuccessRef.current}),
          ...(onErrorRef.current && {onError: onErrorRef.current}),
          nonstopWatching: false,
        });
      },
    });
    runCommand({
      commandKey: COMMAND_KEY.CLICK_SET_BUY,
      action: async commandData => {
        startSetBuyTicketProcess({
          ...commandData,
          ...(onInitRef.current && {onInit: onInitRef.current}),
          ...(onSuccessRef.current && {onSuccess: onSuccessRef.current}),
          ...(onErrorRef.current && {onError: onErrorRef.current}),
        });
      },
    });
  }, [
    param?.disableCommand,
    onInitRef,
    onSuccessRef,
    onErrorRef,
    startSetBuyTicketProcess,
    startTicketProcess,
  ]);

  const state = useSelector(actor, snapshot => snapshot.value);
  const context = useSelector(actor, snapshot => snapshot.context);

  return {
    state,
    context,
    startTicketProcess,
    startSetBuyTicketProcess,
  };
}
